import { createForm } from '@seek/forms-ui';
import type { Locale } from '@seek/melways-sites';
import { maxLength, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Textarea,
  Button,
  Actions,
  Text,
  Box,
  Stack,
  useToast,
  IconCritical,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';

import translations from './.vocab';

import { NOTES_MAX_CONTENT_LENGTH } from './index';

type CallbackFunction = () => void;
type CallbackFunctionWithContent = (content: string) => void;

interface NoteFormValues {
  content: string;
}

interface Props {
  onAdd: CallbackFunctionWithContent;
  showServerError?: boolean;
  loading?: boolean;
  locale: Locale;
}

interface FormProps {
  resetForm: CallbackFunction;
  onAdd: CallbackFunctionWithContent;
  showServerError?: boolean;
  loading?: boolean;
  handleSubmit?: any;
}

const { FormProvider, useField } = createForm<NoteFormValues>();

const CreateNoteForm = ({
  resetForm,
  onAdd,
  handleSubmit,
  loading,
}: FormProps) => {
  const { t } = useTranslations(translations);
  const showToast = useToast();
  const inputEl = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    resetForm();
    inputEl.current?.focus();
  }, [resetForm]);

  const onSubmit = handleSubmit((formValues: NoteFormValues) => {
    onAdd(formValues.content);
    showToast({
      tone: 'positive',
      message: t('Added note'),
      closeLabel: t('Close'),
    });
    resetForm();
  });

  const content = useField({
    id: 'content',
    validators: [maxLength, required] as any,
    validatorProps: {
      max: NOTES_MAX_CONTENT_LENGTH,
      requiredLabel: t('note content'),
    },
  });

  return (
    <div data-testid="create-note-form">
      <form onSubmit={onSubmit}>
        <Stack space="xsmall">
          <Textarea
            id="add-note"
            aria-label={t('Add note')}
            value={content.value}
            tone={content.valid ? 'neutral' : 'critical'}
            onChange={content.onChange}
            onBlur={content.onBlur}
            message={!content.valid && content.errorMessage}
            characterLimit={NOTES_MAX_CONTENT_LENGTH}
            reserveMessageSpace
            ref={inputEl}
          />

          <Box>
            {loading ? (
              <Box data-testid="loader" display={{ tablet: 'inlineBlock' }}>
                <Button size="small" tone="formAccent" loading>
                  {t('Adding')}
                </Button>
              </Box>
            ) : (
              <Actions>
                <Button type="submit" size="small" tone="formAccent">
                  {t('Add')}
                </Button>
                {content.value ? (
                  <Button
                    onClick={resetForm}
                    variant="transparent"
                    tone="neutral"
                    size="small"
                  >
                    {t('Clear')}
                  </Button>
                ) : null}
              </Actions>
            )}
          </Box>
        </Stack>
      </form>
    </div>
  );
};

const CreateNoteFormProvider = observer(
  ({ onAdd, showServerError = false, loading = false, locale }: Props) => {
    const { t } = useTranslations(translations);
    return (
      <Box>
        <FormProvider language={locale} initialValues={{ content: '' }}>
          {({ handleSubmit, resetForm }) => (
            <CreateNoteForm
              loading={loading}
              onAdd={onAdd}
              resetForm={resetForm}
              handleSubmit={handleSubmit}
            />
          )}
        </FormProvider>

        {showServerError && (
          <Box marginTop="small">
            <Text
              tone="critical"
              size="small"
              icon={<IconCritical tone="critical" />}
            >
              {t('Sorry, we couldn’t save your changes. Can we try again?')}
            </Text>
          </Box>
        )}
      </Box>
    );
  },
);

export default CreateNoteFormProvider;
